// Week 6 — Read/write the team doc at shared/team__<code>.
// Wire shape matches vanilla + firestore.rules:
//   { code, memberEmails: string[], elevatedMembers?: string[], createdBy, ... }

import { useEffect, useState } from 'react'
import { onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore'
import { sharedDoc } from '../lib/firebase/firestore'
import { saveUserByEmail, deleteShared } from '../lib/db/shared'
import { useAuth } from './useAuth'
import { useOpts } from './useOpts'

export interface MemberMeta {
  role?: 'admin' | 'pm' | 'crew'
  hourlyRate?: number
  clockEnabled?: boolean
  name?: string
}

export interface TeamDoc {
  code: string
  company?: string
  ownerUid?: string
  createdBy?: string
  memberEmails: string[]
  elevatedMembers?: string[]
  assignmentIds?: string[]
  memberMeta?: Record<string, MemberMeta>
}

export function useTeam(): {
  team: TeamDoc | null
  loading: boolean
  teamCode: string | undefined
} {
  const { uid } = useAuth()
  const opts = useOpts()
  const teamCode = opts.teamCode as string | undefined
  const [team, setTeam] = useState<TeamDoc | null>(null)
  const [loading, setLoading] = useState(!!teamCode)

  useEffect(() => {
    if (!teamCode || !uid) { setTeam(null); setLoading(false); return }
    setLoading(true)
    const ref = sharedDoc(`team__${teamCode}`)
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const d = snap.data() as Partial<TeamDoc>
        setTeam({
          code: teamCode,
          memberEmails: Array.isArray(d.memberEmails) ? d.memberEmails : [],
          ...(d.company ? { company: d.company } : {}),
          ...(d.ownerUid ? { ownerUid: d.ownerUid } : {}),
          ...(d.createdBy ? { createdBy: d.createdBy } : {}),
          ...(Array.isArray(d.elevatedMembers) ? { elevatedMembers: d.elevatedMembers } : {}),
          ...(Array.isArray(d.assignmentIds) ? { assignmentIds: d.assignmentIds } : {}),
          ...(d.memberMeta && typeof d.memberMeta === 'object' ? { memberMeta: d.memberMeta as Record<string, MemberMeta> } : {}),
        })
      } else {
        setTeam(null)
      }
      setLoading(false)
    }, (err) => {
      console.warn('[team] listener error', err)
      setTeam(null)
      setLoading(false)
    })
    return unsub
  }, [teamCode, uid])

  return { team, loading, teamCode }
}

/** 10-character uppercase alphanumeric team code. Mirrors vanilla's entropy. */
export function generateTeamCode(): string {
  const chars = 'ABCDEFGHIJKLMNPQRSTUVWXYZ23456789'
  let out = ''
  for (let i = 0; i < 10; i++) {
    out += chars[Math.floor(Math.random() * chars.length)]
  }
  return out
}

export async function createTeam(params: { code: string; ownerUid: string; ownerEmail: string; company?: string }) {
  const ref = sharedDoc(`team__${params.code}`)
  const payload: Record<string, unknown> = {
    code: params.code,
    ownerUid: params.ownerUid,
    createdBy: params.ownerUid,
    memberEmails: [params.ownerEmail.toLowerCase()],
    _updatedAt: serverTimestamp(),
  }
  if (params.company) payload.company = params.company
  await setDoc(ref, payload)
}

export async function updateTeamMembers(code: string, memberEmails: string[]) {
  const ref = sharedDoc(`team__${code}`)
  await setDoc(ref, {
    memberEmails: memberEmails.map((e) => e.trim().toLowerCase()).filter(Boolean),
    _updatedAt: serverTimestamp(),
  }, { merge: true })
}

/**
 * Add a member to a team: update the team doc AND write the reverse-lookup
 * `user__<email>` doc so that when the invited user signs in, their session
 * joins the team. V1 parity: public/app/index.html L18883 (join-existing
 * signup path) and L19428 (role-change path) both write `user__<email>`.
 * uid is left unset — it's populated on the invitee's next login (Firestore
 * rules restrict uid__ writes to "own doc").
 */
export async function addTeamMemberWithLookup(
  code: string,
  allMemberEmails: string[],
  newEmail: string,
  role: 'admin' | 'pm' | 'crew' | 'project_manager' | 'field_crew' = 'field_crew',
) {
  const email = newEmail.trim().toLowerCase()
  if (!email) return
  await updateTeamMembers(code, allMemberEmails)
  // Map compact roles to V1's teamRole vocabulary.
  const teamRole =
    role === 'pm' ? 'project_manager' :
    role === 'crew' ? 'field_crew' :
    role
  await saveUserByEmail(email, { email, teamCode: code, teamRole })
}

/**
 * Remove a member from a team: update the team doc AND delete the
 * `user__<email>` reverse-lookup doc. V1 parity: public/app/index.html
 * L19522 sets the user's teamCode="" / teamRole="admin" (resets to solo
 * mode). We mirror that by clearing the team fields on the lookup doc so
 * the user's next session doesn't re-join this team.
 *
 * Note: V1 actually overwrites the doc (solo-mode reset) rather than
 * deleting it, because it also clears company/companyType. We do the same
 * — delete would orphan the uid mapping the user still needs for their
 * own solo workspace.
 */
export async function removeTeamMemberWithLookup(
  code: string,
  remainingMemberEmails: string[],
  removedEmail: string,
) {
  const email = removedEmail.trim().toLowerCase()
  if (!email) return
  await updateTeamMembers(code, remainingMemberEmails)
  try {
    // Clear team fields so the user's next login resets to solo mode.
    // Use saveUserByEmail (merge:true) so we don't wipe the `uid` field
    // the user needs for their own data.
    await saveUserByEmail(email, { email, teamCode: '', teamRole: 'admin' })
  } catch (_e) { void 0 }
}

// Re-export so Options.tsx can delete the `user__` doc outright if needed
// (e.g. cancelled invite that never got claimed).
export { deleteShared }

export async function updateElevatedMembers(code: string, elevatedMembers: string[]) {
  const ref = sharedDoc(`team__${code}`)
  await setDoc(ref, {
    elevatedMembers,
    _updatedAt: serverTimestamp(),
  }, { merge: true })
}

export async function updateMemberMeta(code: string, memberMeta: Record<string, MemberMeta>) {
  const ref = sharedDoc(`team__${code}`)
  await setDoc(ref, {
    memberMeta,
    _updatedAt: serverTimestamp(),
  }, { merge: true })
}
